import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Brand, Neutrals } from '@/constants/theme';
import { MAX_RECIPES_PER_USER } from '@/constants/limits';

export type RecipesEmptyVariant = 'no-recipes' | 'no-results' | 'limit';

export interface RecipesEmptyStateProps {
  variant: RecipesEmptyVariant;
  onAddRecipe?: () => void;
  maxRecipes?: number;
}

export function RecipesEmptyState({
  variant,
  onAddRecipe,
  maxRecipes = MAX_RECIPES_PER_USER,
}: RecipesEmptyStateProps) {
  const isNoRecipes = variant === 'no-recipes';
  const isNoResults = variant === 'no-results';
  const isLimit = variant === 'limit';

  const iconName = isNoRecipes || isLimit ? 'restaurant-outline' : 'search';
  const title = isLimit ? 'No recipes yet' : isNoRecipes ? 'No recipes yet' : 'No recipes found';
  const subtitle =
    isLimit || isNoRecipes
      ? 'Add your first recipe to get started'
      : 'Try adjusting your filters';
  const showAddButton = isNoRecipes && !!onAddRecipe;
  const showLimitText = isLimit;

  return (
    <View style={styles.emptyState}>
      <View style={styles.emptyIcon}>
        <Ionicons name={iconName} size={40} color={Neutrals.textMuted} />
      </View>
      <Text style={styles.emptyTitle}>{title}</Text>
      <Text style={styles.emptySubtitle}>{subtitle}</Text>
      {showAddButton && (
        <TouchableOpacity onPress={onAddRecipe} style={styles.emptyButton} activeOpacity={0.8}>
          <Ionicons name="add" size={20} color="#fff" />
          <Text style={styles.emptyButtonText}>Add Recipe</Text>
        </TouchableOpacity>
      )}
      {showLimitText && (
        <Text style={styles.limitText}>
          Maximum of {maxRecipes} recipes. Delete a recipe to add more.
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 24,
    backgroundColor: Neutrals.surface,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    textAlign: 'center',
    marginBottom: 24,
  },
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Brand.primary,
    borderRadius: 12,
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  emptyButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 20,
  },
  limitText: {
    color: Neutrals.textSecondary,
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
  },
});
